import { Component, computed, effect, inject } from '@angular/core';
import {
	BasicFilterExpression,
	CijferMeasure,
	DataOptions,
	DataResponse,
	DataService,
	ExportDataOptions,
	ExportFilter,
} from '../../services/data.service';
import { last, nth, orderBy, sortBy, union } from 'lodash-es';
import { TEKORTPUNTEN_VALUES, VAKORDER_ACTIVE, VAKORDER_DIRECTIONS } from '../../services/weergave-opties';
import { createMeasureColumn, DataRow, getInitialAttributes } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { Observable } from 'rxjs';
import { DuoSelectDropdownComponent, SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { Option } from '@cumlaude/shared-utils';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { ColumnDef, createColumnDef, createDefaultFooterCellDef, TableModel } from '../../shared/components/table/table/table.model';
import { aggDecimals, MultiAggregator, SingleAggregator, sumOver, weightedAverage } from '../../services/aggregation';
import { att } from '../../services/measures';
import { CijferlijstCellComponent } from '../../shared/components/table/cells/cijferlijst-cell/cijferlijst-cell.component';
import { LabelCellComponent } from '../../shared/components/table/cells/label-cell/label-cell.component';
import { PivotTableConfig } from '../../shared/dashboard/pivot-table/pivot-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { getCellPath, PivotTableComponent } from '../../shared/dashboard/pivot-table/pivot-table.component';
import { DataCellComponent } from '../../shared/components/table/cells/data-cell/data-cell.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { CijferlijstData } from '../../services/cijfers';
import { InstellingBron } from '@cumlaude/service-contract';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { LeerlingAantalCellComponent } from '../../shared/components/table/cells/leerling-aantal-cell/leerling-aantal-cell.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { SortDirection } from '../../shared/components/table/table/table.component';

interface CijferlijstI extends Attributes {
	cf_m_nr_tekortpunten: number;
	cf_m_nr_cijfer: number;
	cf_m_nr_decimalen: number;
	clc_m_abb_label_avg: any;
	clc_m_is_voldoende_avg: number;
	cf_m_is_voldoende: number;
	cf_m_nm_bijzonderheid: any;
	cf_nm_kolomtype: any;
}

interface CijferlijstA extends Attributes {
	sum_tekortpunten: number;
	gem_cijfer: number | null;
	decimalen: number;
}

@Component({
	selector: 'app-cijferlijst',
	templateUrl: './cijferlijst.component.html',
	styleUrls: ['./cijferlijst.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		PivotTableComponent,
		FilterBarComponent,
		SingleSelectDropdownComponent,
		DuoSelectDropdownComponent,
		LegendaComponent,
	],
})
export class CijferlijstComponent extends PivotTableConfig<CijferlijstI, CijferlijstA> {
	protected readonly dataService = inject(DataService);

	override fixedAfterGroups = 1;

	readonly tableGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, 1));

	readonly groups = computed(() => [...this.groeperingService.huidigeGroeperingen().slice(1), ['cf_nm_leerling'] as AttrPath]);

	subgroups: AttrPath[] = [['cf_fun_nr_volgnr_vak'], ['cf_abb_vak']];

	cijferlijstFilters: FilterName[] = [
		'cf_nm_schooljaar',
		'cf_co_brin',
		'cf_nm_vestiging',
		'cf_fk_ilt.ilt_nm_niveau',
		'cf_nr_leerjaar',
		'cf_nm_vak',
		'cf_nm_lesgroep',
		'cf_is_alternatievenormering',
		'x_cijferlijst_cijfertype',
		'x_cijferlijst_gem_periode',
	];

	override availableGroups: AttrPath[] = [
		['cf_fun_nm_idu'],
		['cf_nm_klas'],
		['cf_nr_leerjaar'],
		['cf_nm_opleiding'],
		['cf_fk_ilt', 'ilt_nm_niveau'],
		['cf_fk_ilt', 'ilt_nm_opleiding'],
		['cf_fk_ilt', 'ilt_abb_profiel'],
		['cf_nr_leerjaar_vak'],
		['cf_abb_onderwijssoort_vak'],
		['cf_nm_vestiging'],
		['cf_fk_lb_vorig_sj', 'lb_nm_vestiging'],
		['cf_fk_lb', 'lb_nm_leerfase'],
		['cf_fk_lb_vorig_sj', 'lb_nm_leerfase'],
	];

	tekortpuntenKeuze = this.qp.signal('tekortpunten');

	tekortpuntenKeuzeOpties = TEKORTPUNTEN_VALUES.map((val) => new Option(val, val == 0 ? '<Alle>' : val + ' of meer'));

	vakOrderActiveOptions = VAKORDER_ACTIVE.map((val) => new Option(val));

	vakOrderDirectionOptions = VAKORDER_DIRECTIONS.map((val) => new Option(<SortDirection>val, val === 'asc' ? 'Oplopend' : 'Aflopend'));

	vakOrder = this.qp.signal('vakOrder', { active: 'Tekortpunten', direction: 'desc' });

	readonly vakOrderActive = computed(() => {
		const currentActive = this.vakOrder().active;
		return this.vakOrderActiveOptions.find((active) => active.value === currentActive)!;
	});

	readonly vakOrderDirection = computed(() => {
		const currentDirection = this.vakOrder().direction;
		return this.vakOrderDirectionOptions.find((direction) => direction.value === currentDirection)!;
	});

	constructor() {
		super();

		effect(() => {
			this.tekortpuntenKeuze();
			this.vakOrder();
			this.filterService.refresh();
		});

		if (this.userService.bron() === InstellingBron.Magister) {
			this.cijferlijstFilters.push('cf_abb_kolomkop', 'cf_nr_kolom');
		}
	}

	factTable = Table.fac_cf_cijfer;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getCijfersData({
			...options,
			m: [CijferMeasure.STATS, CijferMeasure.LABEL],
			threshold: this.tekortpuntenKeuze(),
		});
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getCijfersExportData(options);
	}

	override createPivotColumns(
		columnRoot: Level<CijferlijstA, number[]>,
		context: DashboardContext<CijferlijstI, CijferlijstA, CijferlijstComponent>
	): ColumnDef<DataRow<CijferlijstA>>[] {
		let r = columnRoot.r;

		const vakOrder = this.vakOrder();
		switch (vakOrder.active) {
			case 'Tekortpunten': {
				r = orderBy(r, [(path) => last(path)!.a.sum_tekortpunten, (path) => last(path)!.a.gem_cijfer], ['asc', 'desc']);
				break;
			}
			case 'Alfabetisch': {
				r = orderBy(r, [(path) => last(path)!.k!.toLowerCase()], ['asc']);
				break;
			}
		}

		const map = r.map((path) => this.createColumn(path, context));
		if (vakOrder.direction === 'desc') return map.reverse();

		return map;
	}

	private createColumn(
		path: Path<CijferlijstA, number[]>,
		context: DashboardContext<CijferlijstI, CijferlijstA, CijferlijstComponent>
	): ColumnDef<DataRow<CijferlijstA>> {
		const vak = last(path)?.k as string;
		const volgnummer = nth(path, -2)?.k as string;

		const colKey = `vak-${volgnummer}-${vak}`;
		const coldef = createColumnDef<DataRow<CijferlijstA>>(colKey);

		coldef.header.component = LabelCellComponent;
		coldef.header.getValue = () => vak;
		coldef.header.class = 'vak-cijfer-header';

		const colIndex = last(path)?.i as number;
		coldef.body.component = CijferlijstCellComponent;
		coldef.body.getValue = this.makeCijferlijstData(colIndex, context);
		coldef.body.class = 'vak-cijfer-column';

		const { sum_tekortpunten, gem_cijfer, decimalen } = getLeafA(path);

		coldef.extraFooter = createDefaultFooterCellDef();
		coldef.extraFooter.component = DataCellComponent;
		coldef.extraFooter.format = '1.0-0';
		coldef.extraFooter.getValue = () => sum_tekortpunten;
		coldef.extraFooter.class = 'value';

		coldef.footer.component = DataCellComponent;
		coldef.footer.format = `1.${decimalen}-${decimalen}`;
		coldef.footer.getValue = () => gem_cijfer;
		coldef.footer.class = 'value';

		return coldef;
	}

	private makeCijferlijstData(
		colIndex: number,
		context: DashboardContext<CijferlijstI, CijferlijstA, CijferlijstComponent>
	): (row: DataRow<CijferlijstA>) => CijferlijstData | null {
		const { subgroupNames, measureNames } = context;
		return (row: DataRow<CijferlijstA>) => {
			const vakPath = getCellPath(row._path, colIndex);
			if (!vakPath) return null;

			return getInitialAttributes<CijferlijstI>(subgroupNames, measureNames, vakPath);
		};
	}

	sortColumn(colPath: (string | null)[], keys0: (string | null)[], keys1: (string | null)[]): (string | null)[] {
		if (colPath.length == 1) return sortBy(union(keys0, keys1), [Number]);
		else return sortBy(union(keys0, keys1));
	}

	private readonly cijferFormat = (path: Path<CijferlijstA, number[]>) => {
		const { decimalen } = getLeafA(path);
		return `1.${decimalen}-${decimalen}`;
	};

	override createMeasureColumns(context: DashboardContext<CijferlijstI, CijferlijstA, CijferlijstComponent>): ColumnDef<DataRow<CijferlijstA>>[] {
		const tekortPunten = createMeasureColumn('TP.', att('sum_tekortpunten'), {
			context,
			headerClass: 'measure-header',
			headerTooltip: 'Tekortpunten',
		});
		tekortPunten.footer.getValue = () => null;

		const gemiddelde = createMeasureColumn('Gem.', att('gem_cijfer'), {
			context,
			format: this.cijferFormat,
			headerClass: 'measure-header',
			headerTooltip: 'Gemiddelde',
		});
		gemiddelde.extraFooter = createDefaultFooterCellDef();
		gemiddelde.extraFooter.getValue = () => 'TP.';
		gemiddelde.extraFooter.class = 'label';
		gemiddelde.extraFooter.tooltip = 'Tekortpunten';
		gemiddelde.footer.component = LabelCellComponent;
		gemiddelde.footer.getValue = () => 'Gem.';
		gemiddelde.footer.class = 'label';
		gemiddelde.footer.tooltip = 'Gemiddelde';

		return [tekortPunten, gemiddelde];
	}

	override getSelectionConfig(context: DataRequestContext<CijferlijstA>): SelectionConfig<CijferlijstA> {
		/**
		 * Er zijn 2 plekken waarop een selectie aangemaakt wordt: de getDataRootSelection-knop in de filterbalk, en de totalen per tabel.
		 * Bij de eerste levert getPathToTableRoot de data-root op, en bij de tweede daadwerkelijk de table-root. Het "r" attribuut komt overeen met de rijen van de tabel(len)
		 * daaronder, en elke rij representeert een andere leerling.
		 */
		const getSize = (path: Path<CijferlijstA, number[]>) => last(this.getPathToTableRoot(context, path))!.r.length;
		const extraFilters =
			this.tekortpuntenKeuze() == 0 ? [] : [new BasicFilterExpression(['cf_fun_sum_tekortpunten'], this.tekortpuntenKeuze(), '>=')];
		return { ...super.getSelectionConfig(context)!, provider: 'cijfers_per_leerling', getSize, extraFilters };
	}

	override enrichTableModel(
		context: DashboardContext<CijferlijstI, CijferlijstA, CijferlijstComponent>,
		tableModel: TableModel<DataRow<CijferlijstA>>
	) {
		tableModel.showExtraFooters = true;

		if (context.groupNames.length == 1) {
			// 'Totaal'-label staat normaal in de footer van de eerste kolom, maar als alle dynamische groeperingen weg zijn gehaald is dit
			// cf_nm_leerling, en daar willen we iets anders in zetten. In de "+" kolom is nog wel plaats...
			tableModel.getColumnDef('addGroup').footer.getValue = () => 'Totaal';
		}

		const path = this.getPathToTableRoot(context, tableModel.data[0]._path);

		const leerlingFooter = tableModel.getColumnDef('cf_nm_leerling').footer;
		leerlingFooter.component = LeerlingAantalCellComponent;
		leerlingFooter.dataType = 'string';
		leerlingFooter.getValue = (_) => {
			const selection = this.createSelection(context, path)!;
			return { displayAantal: `${selection.size} leerling${selection.size == 1 ? '' : 'en'}`, selection };
		};

		leerlingFooter.clickHandler = (_) => {
			const selection = this.createSelection(context, path);
			this.urlService.navigateToSelection(selection);
		};
	}

	getPathToTableRoot(context: DataRequestContext<CijferlijstA>, path: Path<CijferlijstA, number[]>): Path<CijferlijstA, number[]> {
		const tableLevel = context.groupNames.length == 1 ? 0 : 1;
		return path.slice(0, tableLevel + 1);
	}

	getSelectedTekortpuntenOptie() {
		return this.tekortpuntenKeuzeOpties.find((optie) => optie.value === this.tekortpuntenKeuze())!;
	}

	protected override singleAggregators: Partial<{ [ai in keyof CijferlijstA]: SingleAggregator<CijferlijstI, CijferlijstA[ai]> }> = {
		sum_tekortpunten: sumOver('cf_m_nr_tekortpunten'),
		decimalen: aggDecimals('cf_m_nr_decimalen'),
	};

	protected override multiAggregators: MultiAggregator<keyof CijferlijstA, CijferlijstI, CijferlijstA, number | null>[] = [
		weightedAverage('gem_cijfer', 'cf_m_nr_cijfer'),
	];

	override getDisplayOptions(): ExportFilter[] {
		return [{ label: 'Tekortpunten', value: this.tekortpuntenKeuze() ? `${this.tekortpuntenKeuze()} of meer` : 'Alle' }];
	}

	protected updateOptie(veld: string | undefined, richting: SortDirection | undefined) {
		let vakOrder = this.vakOrder();
		if (veld) {
			vakOrder = {
				...vakOrder,
				active: veld,
			};
		}

		if (richting) {
			vakOrder = {
				...vakOrder,
				direction: richting,
			};
		}

		this.qp.dispatch('vakOrder', vakOrder);
	}
}
